import React from 'react';
import { X, ChevronLeft, ChevronRight, Calendar, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const ProjectModal = ({ 
  project, 
  isOpen, 
  onClose, 
  currentImageIndex, 
  onNextImage, 
  onPrevImage, 
  onSetImageIndex,
  onContactClick 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative z-10 bg-white rounded-sm max-w-6xl w-full mx-4 max-h-[95vh] overflow-y-auto transform transition-all duration-300 animate-fade-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-black hover:bg-gray-800 rounded-sm flex items-center justify-center transition-all duration-300 z-20"
        >
          <X className="text-white" size={20} />
        </button>

        {/* Main Image Carousel */}
        <div className="relative h-96 md:h-[500px] overflow-hidden">
          <img
            src={project.images?.[currentImageIndex] || project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          
          {/* Navigation Arrows */}
          {project.images && project.images.length > 1 && (
            <>
              <button
                onClick={onPrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-all duration-300 z-10"
              >
                <ChevronLeft className="text-white" size={24} />
              </button>
              <button
                onClick={onNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-all duration-300 z-10"
              >
                <ChevronRight className="text-white" size={24} />
              </button>
            </>
          )}

          {/* Image Counter */}
          {project.images && project.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {currentImageIndex + 1} / {project.images.length}
            </div>
          )}

          {/* Overlay with basic info */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <div className="flex items-center gap-4 mb-2">
              <Badge className="bg-white text-black">{project.category}</Badge>
              <div className="flex items-center text-white/80 text-sm">
                <Calendar size={16} className="mr-1" />
                {project.year}
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {project.title}
            </h2>
          </div>
        </div>

        {/* Thumbnail Gallery */}
        {project.images && project.images.length > 1 && (
          <div className="p-6 border-b">
            <h4 className="text-lg font-semibold text-black mb-3">Project Gallery</h4>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {project.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => onSetImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-sm overflow-hidden border-2 transition-all duration-200 ${
                    currentImageIndex === index ? 'border-black' : 'border-transparent hover:border-gray-400'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${project.title} - View ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-8">
          {/* Project Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-bold text-black mb-4">Project Overview</h3>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {project.detailedDescription || project.description}
              </p>

              {/* Additional project details */}
              {project.features && (
                <div className="mb-6">
                  <h4 className="text-xl font-bold text-black mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {project.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0" />
                        <span className="text-gray-700 text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Challenges & Solutions */}
              {project.challenges && (
                <div className="mb-6">
                  <h4 className="text-xl font-bold text-black mb-3">Challenges & Solutions</h4>
                  <p className="text-gray-700 text-base leading-relaxed">
                    {project.challenges}
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar - Project Info */}
            <div className="bg-gray-50 rounded-sm p-6 h-fit">
              <h4 className="text-xl font-bold text-black mb-4">Project Details</h4>
              
              <div className="space-y-4">
                <div>
                  <h5 className="font-semibold text-gray-600 text-sm mb-1">CATEGORY</h5>
                  <p className="text-black">{project.category}</p>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-600 text-sm mb-1">YEAR</h5>
                  <p className="text-black">{project.year}</p>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-600 text-sm mb-1">TIMELINE</h5>
                  <p className="text-black">{project.timeline || '3-6 months'}</p>
                </div>
                
                {project.team && (
                  <div>
                    <h5 className="font-semibold text-gray-600 text-sm mb-1">TEAM</h5>
                    <p className="text-black">{project.team}</p>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="mt-6">
                <h5 className="font-semibold text-gray-600 text-sm mb-3">TECHNOLOGIES & SKILLS</h5>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs border-black/20 bg-white">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
            <Button 
              size="lg"
              onClick={onContactClick}
              className="bg-black text-white hover:bg-gray-800 transition-all duration-300 flex-1"
            >
              Start Similar Project
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={onClose}
              className="border-black text-black hover:bg-gray-100 transition-all duration-300 flex-1"
            >
              Close Project
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;